import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Film } from "lucide-react";
import { api } from "@/lib/api";
import type { Club, FinishedMatch } from "@/types";
import { useAuthStore } from "@/store/auth";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Card, Badge } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { Pagination } from "@/components/ui/Pagination";
import { ExportExcelButton } from "@/components/ui/ExportExcelButton";
import { MatchDetailModal } from "@/components/MatchDetailModal";

// GET /matches/finished añade "resultado" (sets del motor nativo, null si no hay) sobre el
// FinishedMatch base de @/types — se amplía aquí en vez de tocar ese archivo compartido.
type FinishedMatchWithResultado = FinishedMatch & { resultado: string | null };

interface PaginatedFinishedMatches {
  data: FinishedMatchWithResultado[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const dateFormatter = new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function MatchCard({ match, onClick }: { match: FinishedMatchWithResultado; onClick: () => void }) {
  return (
    <Card className="flex cursor-pointer flex-col gap-3 transition-shadow hover:shadow-md" onClick={onClick}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-ink-900">{match.court?.name ?? match.courtLabel}</p>
          <p className="text-sm text-ink-400">
            {match.club.nombre} · {match.customerName}
          </p>
        </div>
        {match.hasCamera ? <Badge tone="green">Con vídeo</Badge> : <Badge tone="slate">Sin vídeo</Badge>}
      </div>
      <div className="flex items-center justify-between text-xs text-ink-400">
        <span>{dateFormatter.format(new Date(match.startsAt))}</span>
        <span className="font-medium text-ink-600">{match.resultado ? `Resultado ${match.resultado}` : "Sin resultado"}</span>
      </div>
    </Card>
  );
}

interface PartidosFilters {
  clubId: string;
  pista: string;
  desde: string;
  hasta: string;
}

const emptyPartidosFilters: PartidosFilters = { clubId: "", pista: "", desde: "", hasta: "" };

export function PartidosPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const ownedClubs = useMemo(() => user?.ownedClubs ?? [], [user?.ownedClubs]);
  const [openMatch, setOpenMatch] = useState<FinishedMatchWithResultado | null>(null);
  const [filters, setFilters] = useState<PartidosFilters>(emptyPartidosFilters);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  function updateFilter<K extends keyof PartidosFilters>(key: K, value: PartidosFilters[K]) {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  }

  // En escritorio el popup va bien (vídeo + marcador lado a lado); en móvil un popup así no cabe
  // ni se puede manejar bien, así que ahí se navega a una ficha de partido a pantalla completa.
  function openMatchDetail(match: FinishedMatchWithResultado) {
    if (!match.hasCamera) return;
    if (isMobile) navigate(`/partidos/${match.id}`);
    else setOpenMatch(match);
  }

  const { data: clubs } = useQuery({
    queryKey: ["clubs"],
    queryFn: async () => (await api.get<{ data: Club[] }>("/clubs?limit=100")).data.data,
    enabled: isSuperAdmin,
  });
  const clubFilterOptions = isSuperAdmin ? (clubs ?? []) : ownedClubs.length > 1 ? ownedClubs : [];

  // Filtros sin paginar: para el listado en pantalla (con página) y para que el Excel exporte
  // exactamente lo mismo que está filtrado, pero sin cortar por página.
  const filterParams = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.clubId) params.set("clubId", filters.clubId);
    if (filters.pista) params.set("pista", filters.pista);
    if (filters.desde) params.set("desde", filters.desde);
    if (filters.hasta) params.set("hasta", filters.hasta);
    return params;
  }, [filters]);
  const filterQueryString = filterParams.toString();

  const listQueryString = useMemo(() => {
    const params = new URLSearchParams(filterParams);
    params.set("page", String(page));
    params.set("limit", String(limit));
    return params.toString();
  }, [filterParams, page, limit]);

  const { data: matchesResp, isLoading } = useQuery({
    queryKey: ["matches", "finished", listQueryString],
    queryFn: async () => (await api.get<PaginatedFinishedMatches>(`/matches/finished?${listQueryString}`)).data,
    placeholderData: keepPreviousData,
  });
  const matches = matchesResp?.data ?? [];
  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs items={[{ label: "Partidos" }, { label: "Jugados" }]} />

      <div className="flex items-center gap-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-50 text-violet-600">
          <Film className="h-6 w-6" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Partidos</h1>
          <p className="text-ink-400">Repeticiones de partidos ya jugados: completo, por set o punto a punto.</p>
        </div>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          {clubFilterOptions.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink-600">Club</label>
              <select
                value={filters.clubId}
                onChange={(e) => updateFilter("clubId", e.target.value)}
                className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
              >
                <option value="">Todos</option>
                {clubFilterOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Input
            label="Pista"
            placeholder="Nombre de la pista…"
            value={filters.pista}
            onChange={(e) => updateFilter("pista", e.target.value)}
            className="w-40"
          />
          <Input type="date" label="Desde" value={filters.desde} onChange={(e) => updateFilter("desde", e.target.value)} />
          <Input type="date" label="Hasta" value={filters.hasta} onChange={(e) => updateFilter("hasta", e.target.value)} />
          {hasActiveFilters && (
            <Button
              onClick={() => {
                setFilters(emptyPartidosFilters);
                setPage(1);
              }}
            >
              Limpiar filtros
            </Button>
          )}
        </div>
        <ExportExcelButton url={`/matches/finished/export.xlsx?${filterQueryString}`} filename="partidos-jugados.xlsx" />
      </div>

      {isLoading ? (
        <p className="text-sm text-ink-400">Cargando…</p>
      ) : !matches.length ? (
        <Card className="border-dashed bg-white/60">
          <p className="text-sm text-ink-400">
            {hasActiveFilters ? "Ningún partido coincide con los filtros." : "Todavía no hay partidos con repetición disponible."}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {matches.map((match) => (
            <MatchCard key={match.id} match={match} onClick={() => openMatchDetail(match)} />
          ))}
        </div>
      )}

      <Pagination
        page={matchesResp?.page ?? page}
        totalPages={matchesResp?.totalPages ?? 1}
        total={matchesResp?.total ?? 0}
        onPageChange={setPage}
        limit={limit}
        onLimitChange={(l) => {
          setLimit(l);
          setPage(1);
        }}
      />

      {openMatch && <MatchDetailModal match={openMatch} onOpenChange={(open) => !open && setOpenMatch(null)} />}
    </div>
  );
}
